import * as v from 'valibot'
import { getPhoneValidationI18nKey } from '~/utils/phoneUtils'

const INVALID_EMAIL_I18N_KEY: string = 'validation.invalidEmail'
const REQUIRED_I18N_KEY: string = 'validation.required'

/**
 * Validations de champs (Valibot).
 * Chaque méthode retourne un message d'erreur ou `undefined` si OK.
 */
export class FieldsValidation {
  private constructor() {}

  /**
   * Traduit un message d'erreur en fonction de la clé i18n.
   * @param {string} i18nKey - Clé i18n du message d'erreur.
   * @returns {string} Message d'erreur traduit.
   */
  static translateErrorMessage(i18nKey: string): string {
    const errorMessage: string = useNuxtApp().$i18n.t(i18nKey)
    return errorMessage
  }

  /**
   * Champ requis : la valeur ne doit pas être vide.
   * @param {string} value - Valeur saisie.
   * @returns {string | undefined} Message d'erreur ou `undefined` si OK.
   */
  static isRequired(value: string): string | undefined {
    if (value.trim() === '') return this.translateErrorMessage(REQUIRED_I18N_KEY)
    return undefined
  }

  /**
   * Email : vide = OK, renseigné = doit être un email valide.
   * @param {string} value - Valeur saisie.
   * @returns {string | undefined} Message d'erreur ou `undefined` si OK.
   */
  static emailIsValid(value: string): string | undefined {
    const invalidMessage: string = this.translateErrorMessage(INVALID_EMAIL_I18N_KEY)
    const trimmed: string = value.trim()
    if (trimmed === '') return undefined

    const result = v.safeParse(v.pipe(v.string(), v.email(invalidMessage)), trimmed)
    if (result.success) return undefined

    const firstIssue: v.StringIssue | v.EmailIssue<string> | undefined = result.issues[0]
    if (firstIssue !== undefined && typeof firstIssue.message === 'string') {
      return firstIssue.message
    }
    return invalidMessage
  }

  /**
   * Téléphone : vide = erreur requis, sinon longueur nationale attendue selon l'indicatif.
   * @param {string} prefix - Indicatif pays (ex. `+33`).
   * @param {string} number - Numéro local saisi.
   * @returns {string | undefined} Message d'erreur ou `undefined` si OK.
   */
  static phoneIsValid(prefix: string, number: string): string | undefined {
    const i18nKey: string | null = getPhoneValidationI18nKey(prefix, number)
    if (i18nKey === null) return undefined
    return this.translateErrorMessage(i18nKey)
  }
}
